/**
 * Orion Analytics
 * Financial Intelligence Platform
 *
 * Copyright (c) 2026 Santosh Bag / Orion Analytics Ltd.
 * All rights reserved.
 *
 * Software Development by Cosmo Codeworks Ltd
 *
 * Unauthorized copying, modification, distribution,
 * or commercial use is prohibited.
 */

import React, { useEffect, useState } from "react";

import {
  X,
  Zap,
  BookOpen,
  Shield,
  CheckCircle2,
  Smartphone,
  Clock3,
  HardDrive,
  Download,
} from "lucide-react";


import "swiper/css";
import "swiper/css/pagination";
import oe_home from "../../img/orionedge/oe_home.jpg"
import oe_tools from "../../img/orionedge/oe_tools.jpg"
import oe_analytics1 from "../../img/orionedge/oe_analytics_1.jpg"
import oe_dict from "../../img/orionedge/oe_dict.jpg"
import oe_university from "../../img/orionedge/oe_university.jpg"
const screenshots = [
  oe_home,
  oe_tools,
  oe_analytics1,
  oe_dict,
  oe_university
];

// const screenshots = [
//   "/images/software/orion-edge/home.png",
//   "/images/software/orion-edge/tools.png",
//   "/images/software/orion-edge/payoff.png",
//   "/images/software/orion-edge/reference.png",
//   "/images/software/orion-edge/university.png",
// ];

const OrionEdgeModal = ({ open, onClose }) => {
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (!open) return;

    const timer = setInterval(() => {
      setActive((prev) => (prev + 1) % screenshots.length);
    }, 2500);

    return () => clearInterval(timer);
  }, [open]);

  if (!open) return null;

  const getImage = (offset) => {
    const index =
      (active + offset + screenshots.length) %
      screenshots.length;

    return screenshots[index];
  };

  return (
    <div
      className="
        fixed
        inset-0
        z-[9999]

        bg-black/80
        backdrop-blur-md

        flex
        items-center
        justify-center

        p-5
      "
    >
      <div
        className="
          relative

          w-full
          max-w-[1200px]

          h-[100vh]

          rounded-[28px]

          overflow-hidden

          border
          border-white/10

          bg-[#020817]

          shadow-[0_25px_80px_rgba(0,0,0,0.65)]
        "
      >
        {/* CLOSE */}

        <button
          onClick={onClose}
          className="
            absolute
            top-5
            right-5

            z-[100]

            w-12
            h-12

            rounded-full

            bg-white/5
            hover:bg-white/10

            border
            border-white/10

            flex
            items-center
            justify-center

            transition-all

            text-white
          "
        >
          <X size={24} />
        </button>

        {/* ================================================= */}
        {/* HERO */}
        {/* ================================================= */}

        <div
          className="
            h-[45%]

            border-b
            border-white/10

            bg-gradient-to-br
            from-[#020817]
            via-[#081730]
            to-[#020817]

            grid
            grid-cols-[280px_1fr]
          "
        >
          {/* LEFT PANEL */}

          <div
            className="
              p-8

              flex
              flex-col
              justify-center
            "
          >
            <div
              className="
                text-4xl
                font-black
                tracking-[6px]

                mb-8
              "
            >
              <span className="text-white">
                ORION
              </span>

              <span className="text-blue-500 ml-2">
                EDGE
              </span>
            </div>

            <p
              className="
                text-2xl
                text-slate-200
                leading-relaxed

                mb-8
              "
            >
              Financial Toolkit for
              Smarter Decisions
            </p>

            <div className="space-y-6">

              <Feature
                icon={<Zap size={20} />}
                title="Powerful Calculators"
                text="Trading, Investing, Loans and Personal Finance"
              />

              <Feature
                icon={<BookOpen size={20} />}
                title="Financial Knowledge"
                text="Formulas, Guides, Market Reference & more"
              />

              <Feature
                icon={<Shield size={20} />}
                title="Offline & Secure"
                text="Works offline with private local storage"
              />

            </div>
          </div>

          {/* PHONE SHOWCASE */}

          <div
            className="
              flex
              items-center
              justify-center

              gap-6

              overflow-hidden

              px-8
            "
          >
            <PhoneShot
              image={getImage(-2)}
              size="small"
            />

            <PhoneShot
              image={getImage(-1)}
              size="medium"
            />

            <PhoneShot
              image={getImage(0)}
              size="large"
            />

            <PhoneShot
              image={getImage(1)}
              size="medium"
            />

            <PhoneShot
              image={getImage(2)}
              size="small"
            />
          </div>
        </div>

        {/* ================================================= */}
        {/* BOTTOM */}
        {/* ================================================= */}

        <div
          className="
            h-[60%]

            grid
            lg:grid-cols-[1fr_420px]

            gap-8

            p-8
          "
        >
          {/* ABOUT */}

          <div>
            <h2
              className="
                text-4xl
                font-black

                text-white

                mb-6
              "
            >
              About Orion Edge
            </h2>

            <p
              className="
                text-lg
                text-slate-300

                leading-relaxed

                max-w-4xl

                mb-10
              "
            >
              Orion Edge is a mobile-first
              financial toolkit built for
              traders, investors and individuals
              who need fast, accurate and reliable
              financial calculations and market
              reference information anytime,
              anywhere.
            </p>

            <div
              className="
                grid
                md:grid-cols-2
                gap-y-6
                gap-x-10
              "
            >
              <Bullet text="50+ Financial Calculators" />
              <Bullet text="Financial Formulas & Dictionary" />

              <Bullet text="Options & Trading Tools" />
              <Bullet text="Tax Guide & Market Timings" />

              <Bullet text="Investing & Portfolio Tools" />
              <Bullet text="Offline Access & Data Security" />
            </div>
          </div>

          {/* RIGHT PANEL */}

          <div>
            <div
              className="
                rounded-[24px]

                border
                border-white/10

                bg-white/[0.03]

                p-6

                mb-5
              "
            >
              <SpecRow
                icon={<Smartphone size={18} />}
                label="Platform"
                value="Android"
              />

              <SpecRow
                icon={<Clock3 size={18} />}
                label="Version"
                value="1.0.0"
              />

              <SpecRow
                icon={<Clock3 size={18} />}
                label="Last Updated"
                value="July 2026"
              />

              <SpecRow
                icon={<HardDrive size={18} />}
                label="File Size"
                value="82.6 MB"
              />

              <SpecRow
                icon={<Shield size={18} />}
                label="Requires"
                value="Android 8+"
                last
              />
            </div>

            <a
              href="/downloads/orion-edge.apk"
              download
            >
              <button
                className="
                  w-full

                  py-3

                  rounded-[22px]

                  bg-gradient-to-r
                  from-blue-600
                  to-purple-600

                  text-white

                  text-xl
                  font-bold

                  flex
                  items-center
                  justify-center
                  gap-3

                  hover:scale-[1.01]

                  transition-all
                "
              >
                <Download size={24} />

                Download APK
              </button>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

function PhoneShot({ image, size }) {
  const classes = {
    small:
      "h-[200px] opacity-40 scale-90",
    medium:
      "h-[250px] opacity-70",
    large:
      "h-[350px] scale-110",
  };

  return (
    <img
      src={image}
      alt=""
      className={`
        object-contain

        rounded-[24px]

        transition-all
        duration-500

        ${classes[size]}
      `}
    />
  );
}

function Feature({
  icon,
  title,
  text,
}) {
  return (
    <div className="flex gap-4">
      <div
        className="
          w-12
          h-12

          rounded-full

          bg-blue-600/20

          flex
          items-center
          justify-center

          text-blue-400
        "
      >
        {icon}
      </div>

      <div>
        <div
          className="
            text-white
            font-semibold
          "
        >
          {title}
        </div>

        <div
          className="
            text-sm
            text-slate-400
          "
        >
          {text}
        </div>
      </div>
    </div>
  );
}

function Bullet({ text }) {
  return (
    <div
      className="
        flex
        items-center
        gap-3

        text-slate-200
      "
    >
      <CheckCircle2
        size={18}
        className="text-blue-500"
      />

      {text}
    </div>
  );
}

function SpecRow({
  icon,
  label,
  value,
  last = false,
}) {
  return (
    <div
      className={`
        flex
        justify-between
        items-center

        py-4

        ${
          !last
            ? "border-b border-white/10"
            : ""
        }
      `}
    >
      <div
        className="
          flex
          items-center
          gap-3

          text-slate-400
        "
      >
        {icon}
        {label}
      </div>

      <div
        className="
          text-white
          font-semibold
        "
      >
        {value}
      </div>
    </div>
  );
}

export default OrionEdgeModal;


// import React from "react";
//
// import {
//   X,
//   Zap,
//   BookOpen,
//   Shield,
//   Download,
//   Smartphone,
//   Clock3,
//   HardDrive,
// } from "lucide-react";
//
// import { Swiper, SwiperSlide } from "swiper/react";
// import { Navigation, Pagination, Autoplay } from "swiper/modules";
//
// import "swiper/css";
// import "swiper/css/pagination";
// import oe_home from "../../img/orionedge/oe_home.jpg"
// import oe_tools from "../../img/orionedge/oe_tools.jpg"
// import oe_analytics1 from "../../img/orionedge/oe_analytics_1.jpg"
// import oe_dict from "../../img/orionedge/oe_dict.jpg"
// import oe_university from "../../img/orionedge/oe_university.jpg"
// const screenshots = [
//   oe_home,
//   oe_tools,
//   oe_analytics1,
//   oe_dict,
//   oe_university
// ];
//
// const OrionEdgeModal = ({
//   open,
//   onClose,
// }) => {
//
//   if (!open) return null;
//
//   return (
//
//     <div
//       className="
//         fixed
//         inset-0
//         z-[100]
//
//         bg-black/80
//         backdrop-blur-md
//
//         flex
//         items-center
//         justify-center
//
//         p-6
//       "
//     >
//
//       {/*<div*/}
//       {/*  className="*/}
//       {/*    relative*/}
//
//       {/*    w-full*/}
//       {/*    max-w-[1000px]*/}
//
//       {/*    rounded-[28px]*/}
//
//       {/*    overflow-hidden*/}
//
//       {/*    border*/}
//       {/*    border-blue-900/50*/}
//
//       {/*    bg-[#030816]*/}
//
//       {/*    shadow-[0_30px_80px_rgba(0,0,0,0.55)]*/}
//       {/*  "*/}
//       {/*>*/}
//       <div
//         className="
//           relative
//           w-full
//           max-w-[1200px]
//
//           max-h-[88vh]
//
//           overflow-y-auto
//
//           rounded-[28px]
//
//           border
//           border-blue-900/50
//
//           bg-[#030816]
//         "
//       >
//         {/* CLOSE */}
//
//         <button
//           onClick={onClose}
//           className="
//             absolute
//             top-6
//             right-6
//             z-50
//
//             text-white/70
//             hover:text-white
//           "
//         >
//           <X size={28} />
//         </button>
//
//         {/* ========================================= */}
//         {/* HERO */}
//         {/* ========================================= */}
//
//         <div
//           className="
//             relative
//
//             border-b
//             border-blue-900/40
//
//             bg-gradient-to-br
//             from-[#030816]
//             via-[#07142e]
//             to-[#030816]
//           "
//         >
//
//           <div
//             className="
//               grid
//               grid-cols-[280px_1fr]
//               min-h-[260px]
//             "
//           >
//
//             {/* LEFT */}
//
//             <div
//               className="
//                 p-12
//
//                 flex
//                 flex-col
//                 justify-center
//               "
//             >
//
//               <div
//                 className="
//                   text-5xl
//                   font-black
//                   tracking-[8px]
//                   mb-8
//                 "
//               >
//                 <span className="text-white">
//                   ORION
//                 </span>
//
//                 <span className="text-blue-500 ml-3">
//                   EDGE
//                 </span>
//               </div>
//
//               <div
//                 className="
//                   text-3xl
//                   text-white/90
//                   font-light
//                   leading-relaxed
//                   mb-12
//                 "
//               >
//                 Financial Toolkit for
//                 Smarter Decisions
//               </div>
//
//               <div className="space-y-8">
//
//                 <Feature
//                   icon={<Zap />}
//                   title="Powerful Calculators"
//                   text="Trading, Investing, Loans and Personal Finance"
//                 />
//
//                 <Feature
//                   icon={<BookOpen />}
//                   title="Financial Knowledge"
//                   text="Formulas, Guides, Market Reference & more"
//                 />
//
//                 <Feature
//                   icon={<Shield />}
//                   title="Offline & Secure"
//                   text="Works offline with 100% private data"
//                 />
//
//               </div>
//
//             </div>
//
//             {/* CENTER */}
//
//             <div
//               className="
//                 flex
//                 items-center
//                 justify-center
//                 px-10
//               "
//             >
//
//               <Swiper
//                 modules={[
//                   Pagination,
//                   Autoplay,
//                 ]}
//                 pagination={{
//                   clickable: true,
//                 }}
//                 autoplay={{
//                   delay: 2500,
//                 }}
//                 loop={true}
//                 slidesPerView={3}
//                 centeredSlides={true}
//                 spaceBetween={20}
//               >
//
//                 {screenshots.map((img) => (
//
//                   <SwiperSlide key={img}>
//
//                     <div
//                       className="
//                         flex
//                         justify-center
//                       "
//                     >
//
//                     <img
//                       src={img}
//                       alt=""
//                       className="
//                         h-[600px]
//                         rounded-[32px]
//
//                         shadow-[0_15px_40px_rgba(0,0,0,0.55)]
//
//                         object-contain
//                       "
//                     />
//
//                     </div>
//
//                   </SwiperSlide>
//
//                 ))}
//
//               </Swiper>
//
//             </div>
//
//           </div>
//
//         </div>
//
//         {/* ========================================= */}
//         {/* BOTTOM */}
//         {/* ========================================= */}
//
//           <div
//             className="
//               grid
//               lg:grid-cols-[1fr_430px]
//
//               gap-8
//
//               p-8
//             "
//           >
//
//           {/* ABOUT */}
//
//           <div>
//
//             <h2
//               className="
//                 text-5xl
//                 font-black
//                 text-white
//                 mb-8
//               "
//             >
//               About Orion Edge
//             </h2>
//
//             <p
//               className="
//                 text-xl
//                 text-slate-300
//                 leading-relaxed
//                 mb-10
//               "
//             >
//               Orion Edge is a mobile-first
//               financial toolkit built for
//               traders, investors and individuals
//               who need quick, accurate and
//               reliable financial calculations
//               and market reference anytime,
//               anywhere.
//             </p>
//
//             <div
//               className="
//                 grid
//                 grid-cols-2
//                 gap-5
//               "
//             >
//
//               {[
//                 "50+ Financial Calculators",
//                 "Options & Trading Tools",
//                 "Investing & Portfolio Tools",
//                 "Financial Formulas & Dictionary",
//                 "Tax Guide & Market Timings",
//                 "Offline Access & Data Security",
//               ].map((item) => (
//
//                 <div
//                   key={item}
//                   className="
//                     flex
//                     items-center
//                     gap-3
//
//                     text-slate-200
//                   "
//                 >
//                   <div
//                     className="
//                       w-2
//                       h-2
//
//                       rounded-full
//                       bg-blue-500
//                     "
//                   />
//
//                   {item}
//
//                 </div>
//
//               ))}
//
//             </div>
//
//           </div>
//
//           {/* SPECS */}
//
//           <div>
//
//             <div
//               className="
//                 rounded-[24px]
//
//                 border
//                 border-blue-900/40
//
//                 bg-white/[0.03]
//
//                 p-8
//
//                 mb-6
//               "
//             >
//
//               <Spec
//                 icon={<Smartphone size={18} />}
//                 label="Platform"
//                 value="Android"
//               />
//
//               <Spec
//                 icon={<Clock3 size={18} />}
//                 label="Version"
//                 value="1.0.0"
//               />
//
//               <Spec
//                 icon={<Clock3 size={18} />}
//                 label="Last Updated"
//                 value="July 2026"
//               />
//
//               <Spec
//                 icon={<HardDrive size={18} />}
//                 label="File Size"
//                 value="18.6 MB"
//               />
//
//               <Spec
//                 icon={<Shield size={18} />}
//                 label="Requires"
//                 value="Android 8+"
//               />
//
//             </div>
//
//             <a
//               href="/downloads/orion-edge.apk"
//               download
//             >
//
//               <button
//                 className="
//                   w-full
//
//                   py-6
//
//                   rounded-[22px]
//
//                   bg-gradient-to-r
//                   from-blue-600
//                   to-purple-600
//
//                   text-white
//
//                   font-bold
//                   text-2xl
//
//                   flex
//                   items-center
//                   justify-center
//                   gap-4
//                 "
//               >
//
//                 <Download size={28} />
//
//                 Download APK
//
//               </button>
//
//             </a>
//
//           </div>
//
//         </div>
//
//       </div>
//
//     </div>
//
//   );
// };
//
// function Feature({
//   icon,
//   title,
//   text,
// }) {
//   return (
//     <div className="flex gap-4">
//
//       <div
//         className="
//           w-14
//           h-14
//
//           rounded-full
//
//           bg-blue-600/20
//
//           flex
//           items-center
//           justify-center
//
//           text-blue-400
//         "
//       >
//         {icon}
//       </div>
//
//       <div>
//
//         <div
//           className="
//             text-white
//             font-bold
//             text-xl
//           "
//         >
//           {title}
//         </div>
//
//         <div className="text-slate-400">
//           {text}
//         </div>
//
//       </div>
//
//     </div>
//   );
// }
//
// function Spec({
//   icon,
//   label,
//   value,
// }) {
//   return (
//
//     <div
//       className="
//         flex
//         justify-between
//         items-center
//
//         py-4
//
//         border-b
//         border-white/10
//       "
//     >
//
//       <div
//         className="
//           flex
//           items-center
//           gap-3
//
//           text-slate-400
//         "
//       >
//         {icon}
//         {label}
//       </div>
//
//       <div
//         className="
//           text-white
//           font-semibold
//         "
//       >
//         {value}
//       </div>
//
//     </div>
//
//   );
// }
//
// export default OrionEdgeModal;
//
// import React from "react";
// import { X, Download } from "lucide-react";
//
// const screenshots = [
//   "/img/orionedge/1.png",
//   "/img/orionedge/2.png",
//   "/img/orionedge/3.png",
//   "/img/orionedge/4.png",
//   "/img/orionedge/5.png",
// ];
//
// const OrionEdgeModal = ({
//   open,
//   onClose,
// }) => {
//
//   if (!open) return null;
//
//   return (
//
//     <div
//       className="
//         fixed
//         inset-0
//         z-[9999]
//
//         bg-black/70
//
//         flex
//         items-center
//         justify-center
//
//         p-4
//       "
//     >
//
//       <div
//         className="
//           bg-white
//
//           rounded-[32px]
//
//           max-w-6xl
//           w-full
//
//           overflow-hidden
//
//           max-h-[95vh]
//           overflow-y-auto
//         "
//       >
//
//         {/* HEADER */}
//
//         <div
//           className="
//             flex
//             justify-between
//             items-center
//
//             p-6
//
//             border-b
//           "
//         >
//
//           <h2
//             className="
//               text-3xl
//               font-black
//             "
//           >
//             Orion Edge
//           </h2>
//
//           <button onClick={onClose}>
//             <X />
//           </button>
//
//         </div>
//
//         {/* CAROUSEL */}
//
//         <div
//           className="
//             h-[500px]
//             bg-slate-900
//           "
//         >
//
//           {/* Use your existing carousel here */}
//
//           <div
//             className="
//               flex
//               items-center
//               justify-center
//               h-full
//               text-white
//             "
//           >
//             Mobile Screenshot Carousel
//           </div>
//
//         </div>
//
//         {/* DETAILS */}
//
//         <div
//           className="
//             grid
//             lg:grid-cols-2
//             gap-10
//
//             p-10
//           "
//         >
//
//           <div>
//
//             <h3
//               className="
//                 text-2xl
//                 font-black
//                 mb-5
//               "
//             >
//               Mobile Financial Toolkit
//             </h3>
//
//             <p
//               className="
//                 text-gray-600
//                 leading-relaxed
//               "
//             >
//               Orion Edge is a mobile-first
//               financial toolkit built for
//               investors and traders who need
//               powerful calculators and decision
//               support tools while on the move.
//             </p>
//
//           </div>
//
//           <div>
//
//             <div className="space-y-4">
//
//               <div>
//                 ✓ Position Size Calculator
//               </div>
//
//               <div>
//                 ✓ Risk Reward Calculator
//               </div>
//
//               <div>
//                 ✓ Brokerage Calculator
//               </div>
//
//               <div>
//                 ✓ SIP Calculator
//               </div>
//
//               <div>
//                 ✓ Retirement Planner
//               </div>
//
//               <div>
//                 ✓ Offline Capabilities
//               </div>
//
//             </div>
//
//           </div>
//
//         </div>
//
//         {/* CTA */}
//
//         <div
//           className="
//             px-10
//             pb-10
//           "
//         >
//
//           <a
//             href="/downloads/orion-edge.apk"
//             download
//           >
//
//             <button
//               className="
//                 bg-blue-600
//                 text-white
//
//                 px-8
//                 py-4
//
//                 rounded-2xl
//
//                 font-bold
//
//                 flex
//                 items-center
//                 gap-3
//               "
//             >
//
//               <Download size={20} />
//
//               Download APK
//
//             </button>
//
//           </a>
//
//         </div>
//
//       </div>
//
//     </div>
//
//   );
// };
//
// export default OrionEdgeModal;